import PropTypes from 'prop-types';
import React, { useCallback, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useIntl } from 'react-intl';
import { createFragmentContainer, graphql } from 'react-relay';

import { authenticateUser } from 'src/auth';
import FlashesRenderer from 'src/components/Flashes/FlashesRenderer';
import HTTP404 from 'src/components/HTTP404';
import LayoutErrorBoundary from 'src/components/Layout/LayoutErrorBoundary';
import Loading from 'src/components/Loading';
import CustomerPropertyNavigation from 'src/components/Navigation/CustomerPropertyNavigation';
import Footer from 'src/enosikit/components/Footer';
import { withTracingHOC } from 'src/tracing';

/**
 * Description
 * @param {any} props
 * @returns {React.ReactComponentElement} - CustomerPropertyLayoutContent component
 */
function CustomerPropertyLayoutContent({
  children, property = null, router, viewer = null,
}) {
  const intl = useIntl();

  // Lifting state: when sibling components (CustomerPropertyNavigation and PropertyShow)
  // need to share state, React requires moving that state to their common parent. This
  // DateTime object controls the active date range across the property dashboard and is
  // passed down via props to keep siblings in sync.
  const [datespanStart, setDatespanStart] = useState(null);
  const [datespanFinish, setDatespanFinish] = useState(null);
  const handleDatespanChange = useCallback((newDatespanStart, newDatespanFinish) => {
    setDatespanStart(newDatespanStart);
    setDatespanFinish(newDatespanFinish);
  }, []);

  if (!property) {
    return <HTTP404 />;
  }

  if (!authenticateUser(intl, viewer, null, router)) {
    return <Loading />;
  }

  const propertyTitle = property?.title || intl.formatMessage({ id: 'layout.customer_layout.customer_property_layout.page.fallback_property.title', defaultMessage: 'Property' });
  const pageTitle = intl.formatMessage({ id: 'layout.customer_layout.customer_property_layout.page.title', defaultMessage: 'Enosi - Property: {propertyTitle}' }, { propertyTitle });

  // Clone children and inject date range props
  const childrenWithProps = React.Children.map(children, (child) => {
    if (React.isValidElement(child)) {
      return React.cloneElement(child, {
        datespanFinish,
        datespanStart,
        onDatespanChangeFunc: handleDatespanChange,
      });
    }
    return child;
  });

  return (
    <>
      <Helmet>
        <meta charSet="utf-8" />
        <title>{pageTitle}</title>
      </Helmet>

      <div className="flex flex-column" style={{ minHeight: '80vh' }}>
        <CustomerPropertyNavigation
          viewer={viewer}
          property={property}
          datespanStart={datespanStart}
          datespanFinish={datespanFinish}
        />
        <section id="main">
          <FlashesRenderer />
          <div className="container">
            <div className="flex-auto">{childrenWithProps}</div>
          </div>
        </section>
      </div>

      <Footer versionUI={VERSION} />
    </>
  );
}

const customerPropertyLayoutPropTypes = {
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node,
  ]).isRequired,
  property: PropTypes.shape({
    uuid: PropTypes.string,
    title: PropTypes.string,
  }),
  router: PropTypes.shape({
    replace: PropTypes.func,
  }).isRequired,
  viewer: PropTypes.shape({
    id: PropTypes.string,
  }),
};

CustomerPropertyLayoutContent.propTypes = customerPropertyLayoutPropTypes;

// Intentionally omit @throwOnFieldError here: this layout fragment owns broad page-shell
// concerns such as the <title> tag and auth shell inputs like property.title and
// viewer.viewerUser, so it stays defensive by design with guards, optional chaining, and
// localized fallback values when data is absent or degraded. By contrast, the strict
// CustomerPropertyNavigation fragments are a separate failure domain where corrupted data is
// fatal and should throw. Keeping the layout fragment non-throwing prevents unnecessary
// top-level crashes for fields that can be handled gracefully, while LayoutErrorBoundary
// remains positioned to catch render-phase exceptions from descendant fragments that do opt
// into @throwOnFieldError and to preserve the layout as a stable host for fallback UI.
const CustomerPropertyLayoutFragmentContainer = createFragmentContainer(
  CustomerPropertyLayoutContent,
  {
    viewer: graphql`
      fragment CustomerPropertyLayout_viewer on Viewer {
        id
        viewerUser {
          uuid
          type
        }
        ...CustomerPropertyNavigation_viewer
        ...CustomerPropertyNavigation_properties
      }
    `,
    property: graphql`
      fragment CustomerPropertyLayout_property on Property {
        title
        ...CustomerPropertyNavigation_property
      }
    `,
  },
);

const TracedCustomerPropertyLayout = withTracingHOC(
  'CustomerPropertyLayout',
  CustomerPropertyLayoutFragmentContainer,
);

/**
 * Description
 * @param {any} props
 * @returns {React.ReactComponentElement} - CustomerPropertyLayout component
 */
function CustomerPropertyLayout({
  children, property = null, router, viewer = null,
}) {
  return (
    <LayoutErrorBoundary>
      <TracedCustomerPropertyLayout
        property={property}
        router={router}
        viewer={viewer}
      >
        {children}
      </TracedCustomerPropertyLayout>
    </LayoutErrorBoundary>

  );
}

CustomerPropertyLayout.propTypes = customerPropertyLayoutPropTypes;

export default CustomerPropertyLayout;
